import { useState, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import useEditPost from "../../hooks/useEditPost/useEditPost";
import MentionHashtagInput from "../MentionHashtagInput/MentionHashtagInput";

interface EditPostDialogProps {
	isOpen: boolean;
	onClose: () => void;
	postId: string;
	initialBody: string;
}

const EditPostDialog = ({
	isOpen,
	onClose,
	postId,
	initialBody
}: EditPostDialogProps) => {
	const [body, setBody] = useState(initialBody);
	const { mutate: editPost, isLoading } = useEditPost();

	useEffect(() => {
		if (isOpen) setBody(initialBody);
	}, [isOpen, initialBody]);

	const handleSave = () => {
		if (!body.trim()) return;

		editPost(
			{ id: postId, body: body.trim() },
			{
				onSuccess: () => {
					toast.success(<CustomToast type="Success" message="Post updated" />);
					onClose();
				},
				onError: () => {
					toast.error(
						<CustomToast type="Error" message="Failed to update post" />
					);
				}
			}
		);
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Edit post</DialogTitle>
				</DialogHeader>
				<MentionHashtagInput
					value={body}
					onChange={setBody}
					className="w-full bg-white min-h-[120px] rounded-md border border-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none field-sizing-content"
					autoFocus
					onKeyDown={(e) => {
						if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
							e.preventDefault();
							handleSave();
						}
					}}
				/>
				<div className="flex justify-end gap-2">
					<Button variant="ghost" size="sm" onClick={onClose}>
						Cancel
					</Button>
					<Button
						size="sm"
						disabled={!body.trim() || body.trim() === initialBody || isLoading}
						onClick={handleSave}
					>
						{isLoading ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							"Save"
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default EditPostDialog;
